<template>
    <App title="Update Rider">
        <div class="min-vh-100">

            <!-- Header -->
            <div class="bg-white border-bottom px-4 py-3">
                <div class="d-flex align-items-center justify-content-between mx-auto" style="max-width:860px;">
                    <div class="d-flex align-items-center gap-3">
                        <div class="rounded-circle bg-dark d-flex align-items-center justify-content-center" style="width:40px;height:40px;">
                            <i class="fa fa-motorcycle text-white" style="font-size:15px;"></i>
                        </div>
                        <div>
                            <div class="fw-semibold text-dark" style="font-size:15px;">Update Rider</div>
                            <div class="text-muted" style="font-size:12px;">Edit and update rider information</div>
                        </div>
                    </div>
                    <div class="d-none d-md-flex align-items-center gap-2">
                        <div class="d-flex align-items-center gap-2">
                            <div class="rounded-circle bg-dark text-white d-flex align-items-center justify-content-center fw-semibold" style="width:28px;height:28px;font-size:12px;">1</div>
                            <span class="fw-medium text-dark" style="font-size:13px;">Details</span>
                        </div>
                        <div class="bg-secondary" style="width:32px;height:1px;"></div>
                        <div class="d-flex align-items-center gap-2">
                            <div class="rounded-circle border text-muted d-flex align-items-center justify-content-center" style="width:28px;height:28px;font-size:12px;">2</div>
                            <span class="text-muted" style="font-size:13px;">Documents</span>
                        </div>
                        <div class="bg-secondary" style="width:32px;height:1px;"></div>
                        <div class="d-flex align-items-center gap-2">
                            <div class="rounded-circle border text-muted d-flex align-items-center justify-content-center" style="width:28px;height:28px;font-size:12px;">3</div>
                            <span class="text-muted" style="font-size:13px;">Review</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main card -->
            <div class="py-4">
                <div class="bg-white rounded-0 border mx-auto overflow-hidden w-100">

                    <!-- Profile header row -->
                    <div class="d-flex align-items-center gap-3 px-4 py-3 border-bottom">
                        <div class="rounded-circle overflow-hidden bg-secondary-subtle d-flex align-items-center justify-content-center flex-shrink-0" style="width:48px;height:48px;">
                            <img v-if="avatar" :src="avatar" class="w-100 h-100 object-fit-cover" />
                            <i v-else class="fa fa-user text-secondary fs-5"></i>
                        </div>
                        <div>
                            <div class="fw-semibold text-dark" style="font-size:15px;">{{ firstname || 'New' }} {{ lastname || 'Rider' }}</div>
                            <div class="text-muted" style="font-size:13px;">Rider profile</div>
                        </div>
                        <button type="button" class="btn btn-sm ms-auto border rounded d-flex align-items-center justify-content-center" style="width:32px;height:32px;" @click="confirmAction">
                            <i class="fa fa-times text-muted" style="font-size:12px;"></i>
                        </button>
                    </div>

                    <Form ref="form" @submit="onSubmit">

                        <!-- Personal Information -->
                        <div class="row g-0 border-bottom">
                            <div class="col-12 col-md-4 px-4 py-4 border-end-md">
                                <div class="fw-semibold text-dark mb-1" style="font-size:14px;">Personal</div>
                                <div class="text-muted" style="font-size:13px; line-height:1.5;">Basic details about the rider. Name and contact information.</div>
                            </div>
                            <div class="col-12 col-md-8 px-4 py-4">
                                <div class="row g-3">
                                    <div class="col-6">
                                        <InputField id="firstname" v-model="firstname" name="firstname" rules="required" label="First name" placeholder="First name" />
                                    </div>
                                    <div class="col-6">
                                        <InputField id="lastname" v-model="lastname" name="lastname" rules="required" label="Last name" placeholder="Last name" />
                                    </div>
                                    <div class="col-6">
                                        <InputField id="email" v-model="email" name="email" rules="email" label="Email" type="email" placeholder="rider@example.com" />
                                    </div>
                                    <div class="col-6">
                                        <PhoneField id="phone" v-model="phone" name="phone" rules="required|phone" label="Phone number" placeholder="+234 800 000 0000" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Location -->
                        <div class="row g-0 border-bottom">
                            <div class="col-12 col-md-4 px-4 py-4 border-end-md">
                                <div class="fw-semibold text-dark mb-1" style="font-size:14px;">Location</div>
                                <div class="text-muted" style="font-size:13px; line-height:1.5;">Rider's address and preferred pickup station.</div>
                            </div>
                            <div class="col-12 col-md-8 px-4 py-4">
                                <div class="row g-3">
                                    <div class="col-12">
                                        <InputField id="street" v-model="street" name="street" rules="required" label="Street address" placeholder="e.g 123 Main Street" />
                                    </div>
                                    <div class="col-6">
                                        <SelectField id="country" v-model="country" name="country" rules="required" :options="type_options" label="Country">
                                            <option value="Nigeria" selected>🇳🇬 Nigeria</option>
                                        </SelectField>
                                    </div>
                                    <div class="col-6">
                                        <SelectField id="state" v-model="state" name="state" label="State" :options="state_options" ref="stateField" />
                                    </div>
                                    <div class="col-12">
                                        <SelectField id="station_pick" v-model="station_pick" name="station_pick" label="Station pick" :options="station_options" :disabled="!isSuperAdmin" rules="required" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Identification & Earnings -->
                        <div class="row g-0 border-bottom">
                            <div class="col-12 col-md-4 px-4 py-4 border-end-md">
                                <div class="fw-semibold text-dark mb-1" style="font-size:14px;">Identification</div>
                                <div class="text-muted" style="font-size:13px; line-height:1.5;">BVN and expected monthly earnings for this rider.</div>
                            </div>
                            <div class="col-12 col-md-8 px-4 py-4">
                                <div class="row g-3">
                                    <div class="col-12">
                                        <InputField id="bvn" v-model="bvn" name="bvn" rules="numeric|length:11" label="Bank Verification Number (BVN)" type="text" placeholder="Enter 11-digit BVN" maxlength="11" />
                                    </div>
                                    <div class="col-12">
                                        <MoneyField id="income" v-model="income" name="income" rules="required|numeric|min:0" currency="NGN" locale="en-NG" placeholder="0.00" label="Monthly income (NGN)" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Upload Documents -->
                        <div class="row g-0 border-bottom">
                            <div class="col-12 col-md-4 px-4 py-4 border-end-md">
                                <div class="fw-semibold text-dark mb-1" style="font-size:14px;">Documents</div>
                                <div class="text-muted" style="font-size:13px; line-height:1.5;">Upload required credentials and identification documents.</div>
                            </div>
                            <div class="col-12 col-md-8 px-4 py-4">
                                <div class="row g-3">

                                    <!-- Passport Photo -->
                                    <div class="col-6">
                                        <div v-if="avatar" class="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 border bg-light">
                                            <div class="d-flex align-items-center gap-2">
                                                <i class="fa fa-check-circle text-success"></i>
                                                <div>
                                                    <div class="fw-medium text-dark" style="font-size:13px;">Passport photo</div>
                                                    <div class="text-muted" style="font-size:11px;">Uploaded</div>
                                                </div>
                                            </div>
                                            <button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('avatar')"><i class="fa fa-times"></i></button>
                                        </div>
                                        <Dropzone v-else id="dropzone" name="avatar" label="Passport photo" v-model="avatar" :url="route('upload.ajax')" param="data" rules="required" class="dropzone-minimal" />
                                    </div>

                                    <!-- Government ID -->
                                    <div class="col-6">
                                        <div v-if="identification" class="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 border bg-light">
                                            <div class="d-flex align-items-center gap-2">
                                                <i class="fa fa-check-circle text-success"></i>
                                                <div>
                                                    <div class="fw-medium text-dark" style="font-size:13px;">Government ID</div>
                                                    <div class="text-muted" style="font-size:11px;">Uploaded</div>
                                                </div>
                                            </div>
                                            <button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('identification')"><i class="fa fa-times"></i></button>
                                        </div>
                                        <Dropzone v-else param="data" rules="required" name="identification" v-model="identification" label="Government ID" :url="route('upload.ajax')" class="dropzone-minimal" />
                                    </div>

                                    <!-- Ownership -->
                                    <div class="col-6">
                                        <div v-if="ownership" class="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 border bg-light">
                                            <div class="d-flex align-items-center gap-2">
                                                <i class="fa fa-check-circle text-success"></i>
                                                <div>
                                                    <div class="fw-medium text-dark" style="font-size:13px;">Ownership doc</div>
                                                    <div class="text-muted" style="font-size:11px;">Uploaded</div>
                                                </div>
                                            </div>
                                            <button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('ownership')"><i class="fa fa-times"></i></button>
                                        </div>
                                        <Dropzone v-else param="data" rules="required" name="ownership" v-model="ownership" label="Proof of ownership" :url="route('upload.ajax')" class="dropzone-minimal" />
                                    </div>

                                    <!-- Signature -->
                                    <div class="col-6">
                                        <div v-if="signature" class="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 border bg-light">
                                            <div class="d-flex align-items-center gap-2">
                                                <i class="fa fa-check-circle text-success"></i>
                                                <div>
                                                    <div class="fw-medium text-dark" style="font-size:13px;">Digital signature</div>
                                                    <div class="text-muted" style="font-size:11px;">Uploaded</div>
                                                </div>
                                            </div>
                                            <button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('signature')"><i class="fa fa-times"></i></button>
                                        </div>
                                        <Dropzone v-else param="data" rules="required" name="signature" v-model="signature" label="Digital signature" :url="route('upload.ajax')" class="dropzone-minimal" />
                                    </div>

                                </div>
                            </div>
                        </div>

                        <!-- Legal Documents -->
                        <div class="row g-0 border-bottom">
                            <div class="col-12 col-md-4 px-4 py-4 border-end-md">
                                <div class="fw-semibold text-dark mb-1" style="font-size:14px;">Legal Documents</div>
                                <div class="text-muted" style="font-size:13px; line-height:1.5;">Signed agreement and indemnity form for this rider.</div>
                            </div>
                            <div class="col-12 col-md-8 px-4 py-4">
                                <div class="row g-3">

                                    <!-- Agreement -->
                                    <div class="col-6">
                                        <div v-if="agreement" class="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 border bg-light">
                                            <div class="d-flex align-items-center gap-2">
                                                <i class="fa fa-check-circle text-success"></i>
                                                <div>
                                                    <div class="fw-medium text-dark" style="font-size:13px;">Agreement</div>
                                                    <div class="text-muted" style="font-size:11px;">Uploaded</div>
                                                </div>
                                            </div>
                                            <button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('agreement')"><i class="fa fa-times"></i></button>
                                        </div>
                                        <Dropzone v-else param="data" name="agreement" v-model="agreement" label="Agreement" :url="route('upload.ajax')" class="dropzone-minimal" />
                                    </div>

                                    <!-- Indemnity -->
                                    <div class="col-6">
                                        <div v-if="indemnity" class="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 border bg-light">
                                            <div class="d-flex align-items-center gap-2">
                                                <i class="fa fa-check-circle text-success"></i>
                                                <div>
                                                    <div class="fw-medium text-dark" style="font-size:13px;">Indemnity Form</div>
                                                    <div class="text-muted" style="font-size:11px;">Uploaded</div>
                                                </div>
                                            </div>
                                            <button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('indemnity')"><i class="fa fa-times"></i></button>
                                        </div>
                                        <Dropzone v-else param="data" name="indemnity" v-model="indemnity" label="Indemnity Form" :url="route('upload.ajax')" class="dropzone-minimal" />
                                    </div>

                                </div>
                            </div>
                        </div>

                        <!-- Footer / Actions -->
                        <div class="px-4 py-4">
                            <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                                <div class="form-check p-0 m-0">
                                    <label class="form-check-label d-flex align-items-start gap-2" for="terms" style="font-size:13px; color:#555;">
                                        <input type="checkbox" id="terms" name="terms" v-model="terms" class="form-check-input mt-0 flex-shrink-0" />
                                        <span>I confirm the information is accurate and accept the
                                            <a href="#" class="text-dark fw-medium text-decoration-underline">Terms & Conditions</a>
                                        </span>
                                    </label>
                                </div>

                                <div class="d-flex align-items-center gap-2 flex-shrink-0">
                                    <button type="button" class="btn btn-sm border px-3 py-2 rounded-2 text-muted" style="font-size:13px;" @click="onOpenSidebar" :disabled="!terms">
                                        <i class="fa fa-eye me-1"></i> Preview
                                    </button>
                                    <button type="button" class="btn bg-danger border px-3 py-2 rounded-2 text-light" style="font-size:13px;" @click="goBack">
                                        Cancel
                                    </button>
                                    <Button id="action" type="submit" :label="label" :loading="loading" :done="done" class="btn bg-secondary text-light btn-sm px-4 py-2 rounded-2" style="font-size:13px;" :disabled="!terms" />
                                </div>
                            </div>
                        </div>

                    </Form>
                </div>
            </div>
        </div>

        <SidebarEffect ref="sidebar">
            <Profile :data="previewData" />
        </SidebarEffect>

        <div class="position-fixed top-0 end-0 p-3" style="z-index: 9999;">
            <NotivueKeyboard v-slot="{ containersTabIndex }">
                <Notivue v-slot="item" :containersTabIndex="containersTabIndex">
                    <AppNotification :item="item" />
                </Notivue>
            </NotivueKeyboard>
        </div>
    </App>
</template>

<style scoped>
    @media (min-width: 768px) {
        .border-end-md {
            border-right: 1px solid #dee2e6 !important;
        }
    }

    .dropzone-minimal {
        border: 1.5px dashed #d1d5db;
        border-radius: 10px;
        background: #fafafa;
        transition: border-color 0.2s, background 0.2s;
        min-height: 80px;
    }

    .dropzone-minimal:hover {
        border-color: #6c757d;
        background: #f3f4f6;
    }

    .uploaded-file-card {
        animation: fadeSlideIn 0.25s ease;
    }

    @keyframes fadeSlideIn {
        from { opacity: 0; transform: translateY(6px); }
        to   { opacity: 1; transform: translateY(0); }
    }

    .btn { transition: opacity 0.15s ease; }
    .btn:hover:not(:disabled) { opacity: 0.85; }
</style>

<script lang="ts">
import locations from '@/json/location.json'
import { useConfirm } from '@/libs/useConfirm.ts'
import type Rider from '@/ts/models/Rider'
import Alert from '@/vue/components/Alert.vue'
import AppNotification from '@/vue/components/AppNotification.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Dropzone from '@/widget/Dropzone.vue'
import DropzoneField from '@/vue/components/DropzoneField.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import MoneyField from '@/vue/components/MoneyField.vue'
import PhoneField from '@/vue/components/PhoneField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import SidebarEffect from '@/vue/components/SidebarEffect.vue'
import Profile from '@/vue/components/Profile.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import $ from 'jquery'
import { materialTheme, Notification, NotificationProgress, Notivue, NotivueKeyboard, push } from 'notivue'
import { Options as Select2Options } from 'select2'
import Swal from 'sweetalert2'
import { Component, Prop, Ref, toNative, Vue, Watch } from 'vue-facing-decorator'

@Component({
    components: {
        Block,
        Form,
        InputField,
        PhoneField,
        Button,
        Alert,
        App,
        SelectField,
        DropzoneField,
        Dropzone,
        SidebarEffect,
        Profile,
        MoneyField,
        Notivue,
        Notification,
        NotificationProgress,
        NotivueKeyboard,
        AppNotification,
    },
})
class Update extends Vue {
    loading = false
    done = false
    message = ''

    firstname = ''
    lastname = ''
    email = ''
    phone = ''
    street = ''
    state = ''
    station_pick = ''
    country = ''
    identification = ''
    ownership = ''
    avatar = ''
    bvn = ''
    income = ''
    signature = ''
    agreement = ''
    indemnity = ''
    originalFileName = ''
    terms = false

    materialTheme = materialTheme
    confirm = useConfirm().confirm

    @Prop({ type: Object })
    readonly rider!: Rider

    get isSuperAdmin() {
        return this.$user?.role === 'super_admin'
    }

    get type_options(): Select2Options {
        return {
            placeholder: 'Select Country',
            minimumResultsForSearch: Infinity,
            //@ts-ignore
            ajax: null,
        }
    }

    get station_options(): Select2Options {
        return {
            placeholder: 'Select Station',
            minimumResultsForSearch: Infinity,
            ajax: {
                method: 'get',
                url: this.route('station.ajax.select.station'),
            },
            data: this.rider?.station ? [{
                id: this.rider?.station?.slug,
                text: `${this.rider?.station?.title}`,
                title: `${this.rider?.station?.title}`,
                selected: true,
            }] : [],
            //@ts-ignore
            templateResult: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
            //@ts-ignore
            templateSelection: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
        }
    }

    get state_options(): Select2Options {
        const nigeria = locations.data.find((loc) => loc.country === 'Nigeria')
        return {
            ajax: null,
            placeholder: 'Select State',
            minimumResultsForSearch: Infinity,
            data: nigeria
                ? nigeria.states.map((state) => ({
                    id: state,
                    text: state,
                }))
                : [],
        }
    }

    @Ref('sidebar')
    readonly sidebar!: any

    @Ref('form')
    readonly form!: InstanceType<typeof Form>

    @Ref('alert')
    readonly alert!: InstanceType<typeof Alert>

    @Ref('stateField')
    stateField!: any

    @Ref('phoneField')
    phoneField!: InstanceType<typeof PhoneField>

    @Watch('message')
    onMessageChanged(value: string) {
        if (value.length > 0) {
            this.alert.show()
        } else {
            this.alert.hide()
        }
    }

    handleFileUpload(response: any) {
        this.originalFileName = response.original_name
    }

    reseturl(field: string) {
        (this as any)[field] = ''
    }

    get label(): ButtonLabel {
        return {
            normal: 'Update Rider',
            loading: 'Please wait...',
            done: 'Rider Updated',
        }
    }

    getFilename(url: string): string {
        return url?.split('/').pop() || ''
    }

    async confirmAction() {
        const isConfirmed = await this.confirm('Are you sure you want to cancel this operation?', {
            title: 'Cancel Process',
            confirmText: 'Yes, Cancel',
            cancelText: 'No, Continue',
        })

        if (!isConfirmed) return

        setTimeout(() => {
            this.loading = false
            this.router.visit(this.route('rider.page.group'))
        }, 300)
    }

    //@ts-ignore
    created() {
        this.firstname     = this.rider?.firstname || ''
        this.lastname      = this.rider?.lastname || ''
        this.email         = this.rider?.email || ''
        this.phone         = this.rider?.phone || ''
        this.street        = this.rider?.address?.street || ''
        this.state         = this.rider?.address?.state || ''
        this.country       = this.rider?.address?.country || ''
        this.bvn           = this.rider?.bvn || ''
        this.income        = this.rider?.income?.amount || ''
        this.avatar        = this.rider?.avatar?.url || ''
        this.identification = this.rider?.identification?.url || ''
        this.ownership     = this.rider?.ownership?.url || ''
        this.signature     = this.rider?.signature?.url || ''
        this.agreement     = this.rider?.agreement?.url || ''
        this.indemnity     = this.rider?.indemnity?.url || ''
        this.station_pick  = this.rider?.station?.slug || ''

        setTimeout(() => {
            this.$nextTick(() => {
                const select = this.stateField.$el.querySelector('#state')
                if (select) {
                    $(select).val(this.state).trigger('change')
                }
            })
        }, 3000)
    }

    get previewData() {
        return {
            firstname:      this.firstname,
            lastname:       this.lastname,
            email:          this.email,
            phone:          this.phone,
            street:         this.street,
            state:          this.state,
            country:        this.country,
            bvn:            this.bvn,
            income:         this.income,
            avatar:         this.avatar,
            id_card:        this.identification,
            owner_doc:      this.ownership,
            signature:      this.signature,
            agreement:      this.agreement,
            indemnity:      this.indemnity,
        }
    }

    onOpenSidebar() {
        this.sidebar.show()
    }

    onSubmit(payload: any) {
        this.loading = true
        payload.avatar         = this.avatar
        payload.identification = this.identification
        payload.ownership      = this.ownership
        payload.signature      = this.signature
        payload.agreement      = this.agreement
        payload.indemnity      = this.indemnity

        this.$http
            .put(this.route('rider.ajax.update', { rider: this.rider.id }), payload)
            .then((response) => {
                this.done = true
                this.message = response?.data?.message || 'Rider updated successfully.'
                Swal.fire({
                    title: 'Success!',
                    text: this.message,
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    this.router.visit(this.route('rider.page.group'))
                    this.message = ''
                    Swal.close()
                })
            })
            .catch((e) => {
                this.message = e.response?.data?.message || 'An error occurred.'
                push.error({
                    title: 'Error',
                    message: this.message,
                })
            })
            .finally(() => (this.loading = false))
    }

    goBack() {
        window.history.back()
    }
}

export default toNative(Update)
</script>